import { CascaderValue } from "element-plus";
import { Ref } from "vue";
import {
  FormElementType,
  FileUploadAnswerValue,
  MailingAddressAnswerValue,
  FormResponseAnswer,
  FormElementBase,
  FormResponse,
  NameStampAnswerValue,
  LinkedAnswerAnswerValue,
  RepeaterHostAnswerValue,
  DiscussionAnswerValue,
  ActivityDateAnswerValue,
} from "~/api";

interface FormElementAnswerTypes {
  [FormElementType.ACTION_BUTTON]: null;
  [FormElementType.ANSWER_REVIEW]: number; // number of unanswered questions
  [FormElementType.BOOLEAN]: boolean;
  [FormElementType.CASCADER]: CascaderValue;
  [FormElementType.CHANGE_STATUS_BUTTON]: null;
  [FormElementType.CHECKBOXES]: string[];
  [FormElementType.DATE]: string;
  [FormElementType.DIVIDER]: null;
  [FormElementType.DISCUSSION]: DiscussionAnswerValue;
  [FormElementType.ENTITY]: null;
  [FormElementType.FILE_UPLOAD]: FileUploadAnswerValue[];
  [FormElementType.FORM_TEXT]: string;
  [FormElementType.HEADING]: null;
  [FormElementType.MAILING_ADDRESS]: MailingAddressAnswerValue;
  [FormElementType.MONEY]: number;
  [FormElementType.NAME_STAMP]: NameStampAnswerValue[];
  [FormElementType.NUMBER]: number;
  [FormElementType.RADIO_BUTTONS]: string;
  [FormElementType.REPEATER]: RepeaterHostAnswerValue;
  [FormElementType.RICH_TEXT]: string;
  [FormElementType.SECTION]: null;
  [FormElementType.SECURITY_GROUP]: null;
  [FormElementType.SELECT_LIST]: string;
  [FormElementType.SIMPLE_TEXT]: string;
  [FormElementType.USER_SELECT]: string;
  [FormElementType.LINKED_ANSWER]: LinkedAnswerAnswerValue; // this one will change to accomodate more cases over time

  [FormElementType.APPLICATION_STATUS_SELECT]: string;

  // TODO: what is the answer type for a compliance item?
  // we need an answer for the answer review element to work correctly
  [FormElementType.COMPLIANCE_ITEM]: string;
  [FormElementType.WORKFLOW_ASSOCIATION]: string[];
  [FormElementType.ACTIVITY_DATE]: ActivityDateAnswerValue;

  // the selected person's entity guid
  [FormElementType.PRIMARY_CONTACT_SELECT]: string;

  // the environment name stamped when the response was first opened
  [FormElementType.ENVIRONMENT_INDICATOR]: string;
}
export type AnswerValue<TAnswerType extends FormElementType> =
  FormElementAnswerTypes[TAnswerType];

export type FormAnswerPlus<TAnswerType extends FormElementType> = {
  formElementGuid: FormResponseAnswer["formElementGuid"];
  answerValue: AnswerValue<TAnswerType>;
};

export interface FormResponseAnswerMap {
  [key: string]: {
    formElementGuid: FormResponseAnswer["formElementGuid"];
    answerValue: AnswerValue<keyof FormElementAnswerTypes>;
    isHidden?: boolean;
  };
}

export type UpdateAnswerApiResult<TAnswerType extends FormElementType> = {
  status: "updated" | "deleted" | "error";
  value: AnswerValue<TAnswerType> | null;
};
export type UpdateAnswerApiFunc<TAnswerType extends FormElementType> = (
  newVal: AnswerValue<TAnswerType> | null,
  element: FormElementBase,
  formResponse: FormResponse,
) => Promise<UpdateAnswerApiResult<TAnswerType>>;

export type UseAnswer = <TAnswerType extends FormElementType>(
  /** The element to get the answer ref for */
  element: FormElementBase,
  /** A custom hook that calls the API to update the value */
  apiUpdateFunc?: UpdateAnswerApiFunc<TAnswerType>,
) => Ref<AnswerValue<TAnswerType> | undefined>;

// TODO: is there a better way to do this?
/**
 * Numeric equivalents of FormElementType, sent to the ExpressionValidationHub
 * (useExpressionValidator) where SignalR deserializes them into the SERVER-side
 * FormElementType enum. The values MUST match the declaration order (ordinal)
 * of Server/Forms/Models/Forms/Elements/FormElementType.cs — when adding a new
 * element type, append it here with the server enum's ordinal.
 */
export enum FormElementMappedToNumberType {
  ActionButton = 0,
  Boolean = 1,
  Cascader = 2,
  Checkboxes = 3,
  Date = 4,
  Discussion = 5,
  FileUpload = 6,
  AnswerReview = 7,
  FormText = 8,
  MailingAddress = 9,
  Money = 10,
  Number = 11,
  Heading = 12,
  RadioButtons = 13,
  Repeater = 14,
  RichText = 15,
  Section = 16,
  SecurityGroup = 17,
  SelectList = 18,
  ApplicationStatusSelect = 19,
  SimpleText = 20,
  ChangeStatusButton = 21,
  NameStamp = 22,
  LinkedAnswer = 23,
  ComplianceItem = 24,
  Divider = 25,
  Entity = 26,
  UserSelect = 27,
  WorkflowAssociation = 28,
  ActivityDate = 29,
  PrimaryContactSelect = 30,
  EnvironmentIndicator = 31,
}
